import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AscInfoService } from '../../services/asc-info.service';
import { AscInfoPersonal, AscInfoTimesheet, AscInfoComment } from '../../models/asc-info.model';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-asc-info',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './asc-info.html',
  styleUrls: ['./asc-info.scss']
})
export class AscInfoComponent implements OnInit {
  // 検索条件
  userCode: string = '';  // 条件エリア用（検索用）
  personalUserCode: string = '';  // 個人データ表用（編集用）
  ascUq: string = '';

  // 個人データ
  personal: AscInfoPersonal | null = null;
  email2: string = '';

  // タイムシートデータ
  timesheets: AscInfoTimesheet[] = [];
  showingPastTimesheets: boolean = false;

  // コメントデータ
  comments: AscInfoComment[] = [];
  showingPastComments: boolean = false;

  // バリデーションエラー
  userCodeError: string = '';
  ascUqError: string = '';
  email2Error: string = '';

  // 個人データ表のバリデーションエラー
  personalUserCodeError: string = '';

  // 処理結果メッセージ
  errorMessage: string = '';
  successMessage: string = '';

  // 書き込み権限
  readWrite: boolean = false;
  adminLevel: number = 0;

  // 当月度に未報告タイムシートがあるか（リマインドボタン制御用）
  hasCurrentMonthUnreportedTimesheet: boolean = false;
  private currentMonthYearMonth: string = '';

  // ナビゲーション用イベントエミッター
  @Output() navigateToJobOrder = new EventEmitter<number>();
  @Output() navigateToTs = new EventEmitter<{ ascUq?: string; userCode?: string; yearMonth?: string; timesheetId?: number }>();
  @Output() navigateToCustomer = new EventEmitter<number>();

  // 元の値を保持（変更検知用）
  originalUserCode: string = '';
  originalEmail2: string = '';

  constructor(
    private ascInfoService: AscInfoService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    // ReadWriteを取得
    this.readWrite = this.authService.getReadWrite();
    this.adminLevel = this.authService.getAdminLevel() || 0;

    // Clear messages on tab open
    this.errorMessage = '';
    this.successMessage = '';

    // 保存されたデータを復元
    const savedData = this.ascInfoService.getSavedData();
    if (savedData) {
      // 旧形式（3つに分かれている場合）からの変換をサポート
      if (savedData.userCode) {
        this.userCode = savedData.userCode;
      } else if (savedData.userCode1 || savedData.userCode2 || savedData.userCode3) {
        const code1 = savedData.userCode1 || '';
        const code2 = savedData.userCode2 || '';
        const code3 = savedData.userCode3 || '';
        if (code1 || code2 || code3) {
          this.userCode = `${code1}-${code2}-${code3}`;
        }
      }
      this.ascUq = savedData.ascUq;
      this.personal = savedData.personal;
      this.email2 = savedData.email2;
      this.timesheets = savedData.timesheets;
      this.currentMonthYearMonth = this.getTargetYearMonth();
      this.updateUnreportedFlag();
      this.comments = savedData.comments;
      this.showingPastTimesheets = savedData.showingPastTimesheets;
      this.showingPastComments = savedData.showingPastComments;
    }
  }

  private updateUnreportedFlag(): void {
    const ym = this.currentMonthYearMonth;
    const year = parseInt(ym.substring(0, 4));
    const month = parseInt(ym.substring(4, 6));
    const prevYear  = month === 1 ? year - 1 : year;
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevMonthYearMonth = `${prevYear}${prevMonth.toString().padStart(2, '0')}`;

    this.hasCurrentMonthUnreportedTimesheet = this.timesheets
      .some(ts =>
        (ts.yearMonth === ym || ts.yearMonth === prevMonthYearMonth)
        && ts.editing > 0
      );
  }

  // 登録番号のバリデーション
  validateUserCode(): boolean {
    const code = this.userCode.trim();

    if (!code) {
      this.userCodeError = '';
      return true; // 空の場合はOK（UQで検索する可能性があるため)
    }

    // ハイフンを除去して数字のみをチェック
    const digitsOnly = code.replace(/-/g, '');
    
    if (!/^\d{9}$/.test(digitsOnly)) {
      this.userCodeError = '登録番号は9桁の数字で入力してください（XXX-XX-XXXX）';
      return false;
    }

    this.userCodeError = '';
    return true;
  }

  // ASC UQのバリデーション
  validateAscUq(): boolean {
    if (!this.ascUq.trim()) {
      this.ascUqError = '';
      return true; // 空の場合はOK（登録番号で検索する可能性があるため）
    }

    if (!/^\d{9}$/.test(this.ascUq)) {
      this.ascUqError = 'ASC UQは9桁の数字で入力してください';
      return false;
    }

    this.ascUqError = '';
    return true;
  }

  // メールアドレスのバリデーション
  validateEmail2(): boolean {
    if (!this.email2.trim()) {
      this.email2Error = '';
      return true;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.email2)) {
      this.email2Error = '有効なメールアドレスを入力してください';
      return false;
    }

    this.email2Error = '';
    return true;
  }

  // 個人データ表の登録番号バリデーション
  validatePersonalUserCode(): boolean {
    const code = this.personalUserCode.trim();

    if (!code) {
      this.personalUserCodeError = '';
      return true;
    }

    // ハイフンを除去して数字のみをチェック
    const digitsOnly = code.replace(/-/g, '');
    
    if (!/^\d{9}$/.test(digitsOnly)) {
      this.personalUserCodeError = '登録番号は9桁の数字で入力してください（XXX-XX-XXXX）';
      return false;
    }

    this.personalUserCodeError = '';
    return true;
  }

  // 修正ボタンが有効かチェック
  get isModifyButtonEnabled(): boolean {
    if (!this.personal) return false;

    const currentUserCode = this.personalUserCode;
    const isUserCodeChanged = currentUserCode !== this.originalUserCode;
    const isEmail2Changed = this.email2 !== this.originalEmail2;

    // 変更があり、エラーがない場合に有効
    return (isUserCodeChanged || isEmail2Changed) && 
           !this.personalUserCodeError && 
           !this.email2Error;
  }

  // リマインドボタンが無効かどうか（readWrite、adminLevel、当月未報告存在チェック）
  get isReminderButtonDisabled(): boolean {
    return !this.readWrite || this.adminLevel < 2 || !this.hasCurrentMonthUnreportedTimesheet;
  }

  // 入力フィールドのキーダウンイベント（数字のみ）
  onNumberKeydown(event: KeyboardEvent): boolean {
    const allowedKeys = ['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight'];
    
    // Ctrl+VまたはCmd+V（ペースト）を許可
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      return true;
    }
    
    if (allowedKeys.includes(event.key)) {
      return true;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // 登録番号入力時の処理（自動フォーマット）
  onUserCodeInput(event: Event): void {
    if ((event as InputEvent).isComposing) return;
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // 数字のみを抽出
    const digitsOnly = value.replace(/\D/g, '');
    
    // 自動フォーマット: XXX-XX-XXXX
    let formatted = '';
    if (digitsOnly.length > 0) {
      formatted = digitsOnly.substring(0, 3);
      if (digitsOnly.length > 3) {
        formatted += '-' + digitsOnly.substring(3, 5);
      }
      if (digitsOnly.length > 5) {
        formatted += '-' + digitsOnly.substring(5, 9);
      }
    }
    
    // DOM を直接更新してから ngModel を更新する
    // （[(ngModel)] の DefaultValueAccessor が同じ input イベントで
    //   onChange(input.value) を呼び出し raw 値で上書きするのを防ぐため）
    input.value = formatted;
    this.userCode = formatted;
    
    // 登録番号に入力があればASC UQをクリア
    if (this.userCode) {
      this.ascUq = '';
      this.ascUqError = '';
    }
  }

  // ASC UQ入力時の処理
  onAscUqInput(): void {
    // ASC UQに入力があれば登録番号をクリア
    if (this.ascUq) {
      this.userCode = '';
      this.userCodeError = '';
    }
  }

  // 表示ボタンクリック
  onDisplayClick(): void {
    // メッセージをクリア
    this.errorMessage = '';
    this.successMessage = '';

    // バリデーション
    const isUserCodeValid = this.validateUserCode();
    const isAscUqValid = this.validateAscUq();

    if (!isUserCodeValid || !isAscUqValid) {
      return;
    }

    // 両方未入力チェック
    const hasUserCode = this.userCode.trim();
    const hasAscUq = this.ascUq.trim();

    if (!hasUserCode && !hasAscUq) {
      alert('登録番号またはUQを入力してください');
      return;
    }

    // API呼び出し
    const userId = hasAscUq ? parseInt(this.ascUq) : undefined;
    const request = {
      userCode: hasUserCode ? this.userCode : undefined,
      userId: (userId && !isNaN(userId)) ? userId : undefined
    };

    this.ascInfoService.getAscInfo(request).subscribe({
      next: (response) => {
        if (response.personal) {
          this.personal = response.personal;
          this.email2 = response.personal.email2 || '';
          
          // 登録番号をフォーマット付きでセット
          this.userCode = response.personal.userCode || '';
          this.personalUserCode = response.personal.userCode || '';
          
          // 元の値を保存（変更検知用）
          this.originalUserCode = response.personal.userCode;
          this.originalEmail2 = this.email2;
          
          // UQ表示
          this.ascUq = response.personal.userId.toString();

          // タイムシートとコメント
          this.timesheets = response.timesheets;
          // 当月度に未報告タイムシートがあるかチェック（リマインドボタン制御用）
          this.currentMonthYearMonth = this.getTargetYearMonth();
          this.updateUnreportedFlag();
          this.comments = response.comments;
          this.showingPastTimesheets = false;
          this.showingPastComments = false;
          
          // 個人データ表のバリデーションエラーをクリア
          this.personalUserCodeError = '';
          
          // データをサービスに保存
          this.saveData();
        }
      },
      error: (error) => {
        if (error.status === 404) {
          this.clearDataExceptInput();
          this.errorMessage = 'ユーザーが見つかりませんでした';
        } else {
          this.errorMessage = 'データの取得に失敗しました。もう一度お試しください。';
        }
      }
    });
  }

  // 過去のタイムシートリンククリック
  onPastTimesheetsClick(): void {
    if (!this.personal) return;

    // メッセージをクリア
    this.errorMessage = '';
    this.successMessage = '';

    this.ascInfoService.getTimesheets(this.personal.userId, true).subscribe({
      next: (timesheets) => {
        this.timesheets = timesheets;
        this.showingPastTimesheets = true;
        this.saveData();
      },
      error: () => {
        alert('過去のタイムシートの取得に失敗しました');
      }
    });
  }

  // 直近のタイムシートリンククリック
  onRecentTimesheetsClick(): void {
    if (!this.personal) return;

    // メッセージをクリア
    this.errorMessage = '';
    this.successMessage = '';

    this.ascInfoService.getTimesheets(this.personal.userId, false).subscribe({
      next: (timesheets) => {
        this.timesheets = timesheets;
        // 当月度に未報告タイムシートがあるかチェック
        this.updateUnreportedFlag();
        this.showingPastTimesheets = false;
        this.saveData();
      }
    });
  }

  // 過去のコメントリンククリック
  onPastCommentsClick(): void {
    if (!this.personal) return;

    // メッセージをクリア
    this.errorMessage = '';
    this.successMessage = '';

    this.ascInfoService.getComments(this.personal.userId, true).subscribe({
      next: (comments) => {
        this.comments = comments;
        this.showingPastComments = true;
        this.saveData();
      },
      error: () => {
        alert('過去のコメントの取得に失敗しました');
      }
    });
  }

  // 直近のコメントリンククリック
  onRecentCommentsClick(): void {
    if (!this.personal) return;

    // メッセージをクリア
    this.errorMessage = '';
    this.successMessage = '';

    this.ascInfoService.getComments(this.personal.userId, false).subscribe({
      next: (comments) => {
        this.comments = comments;
        this.showingPastComments = false;
        this.saveData();
      },
      error: () => {
        alert('直近のコメントの取得に失敗しました');
      }
    });
  }

  // データをサービスに保存
  private saveData(): void {
    this.ascInfoService.saveData({
      userCode: this.userCode,
      ascUq: this.ascUq,
      personal: this.personal,
      email2: this.email2,
      timesheets: this.timesheets,
      comments: this.comments,
      showingPastTimesheets: this.showingPastTimesheets,
      showingPastComments: this.showingPastComments
    });
  }

  // クリアボタンクリック
  onClearClick(): void {
    this.userCode = '';
    this.personalUserCode = '';
    this.ascUq = '';
    this.personal = null;
    this.email2 = '';
    this.timesheets = [];
    this.hasCurrentMonthUnreportedTimesheet = false;
    this.comments = [];
    this.showingPastTimesheets = false;
    this.showingPastComments = false;
    this.userCodeError = '';
    this.ascUqError = '';
    this.email2Error = '';
    
    // サービスのデータもクリア
    this.ascInfoService.clearData();
  }

  // 入力欄以外をクリア
  private clearDataExceptInput(): void {
    // メッセージをクリア
    this.errorMessage = '';
    this.successMessage = '';

    this.personal = null;
    this.email2 = '';
    this.timesheets = [];
    this.hasCurrentMonthUnreportedTimesheet = false;
    this.comments = [];
    this.personalUserCodeError = '';
    this.originalUserCode = '';
    this.originalEmail2 = '';
  }

  // パスワードリセットボタンクリック
  onPasswordResetClick(): void {
    if (!this.personal) return;

    if (!confirm(`${this.personal.userName}さんのパスワードをリセットします。よろしいですか?`)) {
      return;
    }

    this.ascInfoService.resetPassword(this.personal.userId).subscribe({
      next: () => {
        alert('パスワードをリセットしました。');
        this.refreshPersonalData();
      },
      error: () => {
        alert('パスワードのリセットに失敗しました。');
      }
    });
  }

  // メールアドレスリセットボタンクリック
  onEmailResetClick(): void {
    if (!this.personal) return;

    if (!confirm('指定メールアドレスをリセットします。よろしいですか?')) {
      return;
    }

    this.ascInfoService.resetEmail(this.personal.userId).subscribe({
      next: () => {
        alert('メールアドレスをリセットしました。');
        this.refreshPersonalData();
      },
      error: () => {
        alert('指定メールアドレスのリセットに失敗しました。');
      }
    });
  }

  // 修正ボタンクリック
  onModifyClick(): void {
    if (!this.personal) return;

    // バリデーション
    const isUserCodeValid = this.validatePersonalUserCode();
    const isEmail2Valid = this.validateEmail2();

    if (!isUserCodeValid || !isEmail2Valid) {
      return;
    }

    // Clear previous messages
    this.errorMessage = '';
    this.successMessage = '';

    // サーバー側で重複チェックを一元実施するため直接更新を呼ぶ
    this.performUpdate();
  }

  // 実際の更新処理
  private performUpdate(): void {
    if (!this.personal) return;

    // Clear previous messages
    this.errorMessage = '';
    this.successMessage = '';

    if (!confirm('ユーザー情報を更新してもよろしいですか?')) {
      return;
    }

    const userCode = this.personalUserCode;

    this.ascInfoService.updateUser(this.personal.userId, userCode, this.email2).subscribe({
      next: (response) => {
        this.successMessage = 'ユーザー情報を更新しました。';
        // 最終更新日を更新し、元の値も更新
        this.refreshPersonalData();
      },
      error: (error) => {
        if (error.error?.message === 'そのメールアドレスは既に使用されています') {
          this.errorMessage = error.error.message;
        } else {
          this.errorMessage = 'ユーザー情報の更新に失敗しました。';
        }
      }
    });
  }

  // 個人データを再取得
  private refreshPersonalData(): void {
    if (!this.personal) return;

    const request = {
      userId: this.personal.userId
    };

    this.ascInfoService.getAscInfo(request).subscribe({
      next: (response) => {
        if (response.personal) {
          this.personal = response.personal;
          this.email2 = response.personal.email2 || '';
          
          // 登録番号をフォーマット付きでセット
          this.userCode = response.personal.userCode || '';
          this.personalUserCode = response.personal.userCode || '';
          
          // 元の値を更新
          this.originalUserCode = response.personal.userCode;
          this.originalEmail2 = this.email2;
          
          this.saveData();
        }
      },
      error: () => { }
    });
  }

  // リマインドボタンクリック
  onReminderClick(): void {
    if (!this.personal) return;
    if (!this.hasCurrentMonthUnreportedTimesheet) return;

    if (!confirm('リマインドメールを送信します。よろしいですか？')) {
      return;
    }

    this.ascInfoService.sendReminder(
      this.personal.userId,
      this.currentMonthYearMonth
    ).subscribe({
      next: (response) => {
        alert('リマインドメールを送信しました');
        // 送信後は個人データを再取得（最終リマインド日時が更新される）
        this.refreshPersonalData();
      },
      error: () => {
        alert('リマインドメールの送信に失敗しました');
      }
    });
  }

  // 締日の表示形式を取得
  getCloseDateDisplay(closeDate: number): string {
    return closeDate === 15 ? '15日' : '末日';
  }

  // 対象年月を返す: 平日5日目＋月別オフセット以降なら当月、それ以前なら先月 (yyyyMM形式)
  private getTargetYearMonth(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-based
    const day = today.getDate();

    const monthOffset: { [key: number]: number } = {
      0:  3,  // 1月 +3
      4:  3,  // 5月 +3
      10: 1,  // 11月 +1
    };
    const offset = monthOffset[month] ?? 0;

    let weekdayCount = 0;
    let fifthWeekdayDate = 0;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = new Date(year, month, d).getDay();
      if (dow !== 0 && dow !== 6) {
        weekdayCount++;
        if (weekdayCount === 5) { fifthWeekdayDate = d; break; }
      }
    }

    const threshold = fifthWeekdayDate + offset;
    if (day >= threshold) {
      return `${year}${(month + 1).toString().padStart(2, '0')}`;
    } else {
      const prevMonth = month === 0 ? 12 : month;
      const prevYear  = month === 0 ? year - 1 : year;
      return `${prevYear}${prevMonth.toString().padStart(2, '0')}`;
    }
  }
  // ナビゲーションメソッド
  onJobOrderClick(jobOrderId: number): void {
    this.navigateToJobOrder.emit(jobOrderId);
  }

  onTimesheetClick(timesheet: AscInfoTimesheet): void {
    this.navigateToTs.emit({
      ascUq: this.ascUq,
      userCode: this.userCode,
      yearMonth: timesheet.yearMonth,
      timesheetId: timesheet.timesheetId
    });
  }

  onCustomerOfficeClick(customerOfficeId: number): void {
    this.navigateToCustomer.emit(customerOfficeId);
  }

  // 外部からの呼び出し用の公開メソッド
  public loadByAscUq(ascUq: string): void {
    this.ascUq = ascUq;
    this.userCode = '';
    this.onDisplayClick();
  }

  public loadByUserCode(userCode: string): void {
    this.userCode = userCode;
    this.ascUq = '';
    this.onDisplayClick();
  }
}
